import {
  ArrowLeftOutlined,
  CheckOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  InfoCircleFilled,
  LoadingOutlined,
  QrcodeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toDataURL } from 'qrcode';

import { ApiError, api, MAX_PAGE_SIZE, newIdempotencyKey, type PageInfo } from '../api/client';
import { reportApi } from '../api/reportClient';
import type { components } from '../api/schema.gen';
import { AutoFallbackToggle } from '../components/AutoFallbackToggle';
import { DateField } from '../components/DateField';
import { EmptyState } from '../components/EmptyState';
import { FirstTopUpPrompt } from '../components/FirstTopUpPrompt';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { CursorPagePager } from '../components/Pagination';
import { PromoCodeRedeem } from '../components/PromoCodeRedeem';
import { QRCard } from '../components/QRCard';
import { SelectMenu } from '../components/SelectMenu';
import { SlipUploadModal } from '../components/SlipUploadModal';
import { WHTClaimModal } from '../components/WHTClaimModal';
import { JcbMark, MastercardMark, PromptPayMark, VisaMark } from '../components/PaymentBrandMarks';
import { useToast } from '../components/Toast';
import { formatDateTime, formatIsoDate } from '../lib/dateFormat';
import { parseIsoDay, todayIsoDay, toIsoDay } from '../lib/calendar';
import { downloadQrImage } from '../lib/downloadQrImage';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { MoneyParseError, formatBalance, formatTHB, grossUpVAT7, toDecimalString, toMicro } from '../lib/money';
import { OmiseSourceError, OmiseUnavailableError, createCardToken, createPromptPaySource } from '../lib/omise';
import { fadeUp } from '../lib/motionPresets';
import { isValidTaxId, normalizeTaxId } from '../lib/taxId';
import { useSession } from '../lib/SessionContext';

type TaxInvoiceInput = components['schemas']['TaxInvoiceInput'];
type Province = components['schemas']['Province'];
type District = components['schemas']['District'];
type Subdistrict = components['schemas']['Subdistrict'];
type TaxInvoiceProfile = components['schemas']['TaxInvoiceProfile'];

const BRANCH_CODE_RE = /^\d{5}$/;

// Local-time YYYYMMDD-HHMMSS, e.g. "20260831-181117" — used for saved-QR
// file names (see saveQrCode) so a second save a moment later never
// silently overwrites the first.
function fileTimestamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

// Marks a required field's label — every field on this form that carries
// `required` (or blocks submit when empty, like the custom amount) gets one.
function Req() {
  return (
    <span className="required-mark" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

type TopUpResource = components['schemas']['TopUp'];

// Presets stay at or above the server minimum (member-api minTopUpTHB = 300).
const PRESETS = ['300.00', '500.00', '1000.00', '2000.00'];
const MIN_TOP_UP_THB = 300;
const MIN_TOP_UP = `${MIN_TOP_UP_THB}.00`;
const POLL_MS = 4000;
const DEFAULT_HISTORY_DAYS = 30;
const DEFAULT_HISTORY_PAGE_SIZE = 10;
/** Must match member-api `topUpTTL` (billing.go) — used only for the pre-pay form hint. */
const QR_TTL_MINUTES = 30;

function formatCountdown(msLeft: number): string {
  const totalSec = Math.max(0, Math.floor(msLeft / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Live mm:ss until `expiresAt`; `expired` is true once the clock hits zero. */
function useQrCountdown(expiresAt: string | null | undefined): { label: string | null; expired: boolean } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return { label: null, expired: false };
  const msLeft = new Date(expiresAt).getTime() - now;
  return { label: formatCountdown(msLeft), expired: msLeft <= 0 };
}

function defaultHistoryFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - (DEFAULT_HISTORY_DAYS - 1));
  return todayIsoDay(d);
}
function rangeStart(dateStr: string): string {
  return `${dateStr}T00:00:00Z`;
}
/** Exclusive upper bound — matches GET /topups `created_at < to`. */
function rangeEnd(dateStr: string): string {
  const d = parseIsoDay(dateStr);
  if (!d) return `${dateStr}T23:59:59Z`;
  const next = new Date(d.year, d.month - 1, d.day + 1);
  return `${toIsoDay(next.getFullYear(), next.getMonth() + 1, next.getDate())}T00:00:00Z`;
}

function topUpInRange(t: TopUpResource, from: string, to: string): boolean {
  const ms = Date.parse(t.created_at);
  return ms >= Date.parse(rangeStart(from)) && ms < Date.parse(rangeEnd(to));
}

function filterTopUpsByRange(rows: TopUpResource[], from: string, to: string): TopUpResource[] {
  return rows.filter((t) => topUpInRange(t, from, to));
}

async function fetchAllTopUps(): Promise<TopUpResource[]> {
  const all: TopUpResource[] = [];
  let cursor: string | undefined;
  for (;;) {
    const res = await api.topUps({ limit: MAX_PAGE_SIZE, cursor });
    all.push(...res.data);
    if (!res.page.has_more || !res.page.next_cursor) break;
    cursor = res.page.next_cursor;
  }
  return all;
}

function clientHistoryPage(
  rows: TopUpResource[],
  targetPage: number,
  size: number,
): { slice: TopUpResource[]; page: PageInfo } {
  const total = rows.length;
  const start = (targetPage - 1) * size;
  const slice = rows.slice(start, start + size);
  return {
    slice,
    page: {
      has_more: start + size < total,
      total,
      next_cursor: undefined,
    },
  };
}
// LLMR-90: the draft invoice is generated by a background goroutine on the
// server, AFTER the webhook already answered "credited" — so `status`
// reaching 'paid' does not mean `invoice_id` is set yet. Keep polling a
// short while past 'paid' so the history row's download button appears
// without the customer having to reload the page. 6 * POLL_MS = 24s, well
// past a normal PDF render.
const MAX_PAID_POLLS_FOR_INVOICE = 6;

/** Digits and at most one '.', with up to 2 fractional places (invoice precision). */
function sanitizeAmountInput(raw: string): string {
  const stripped = raw.replace(/[^\d.]/g, '');
  const dot = stripped.indexOf('.');
  if (dot === -1) return stripped;
  const whole = stripped.slice(0, dot);
  const frac = stripped
    .slice(dot + 1)
    .replace(/\./g, '')
    .slice(0, 2);
  return `${whole}.${frac}`;
}

export function TopUp() {
  const { ready, hasOrg, balance, balanceUpdatedAt, refreshBalance, canManageBilling, isB2BOrg, me } = useSession();
  const { dict, locale } = useLanguage();
  const { showToast } = useToast();
  const [onboarding, setOnboarding] = useState(false);

  const [amount, setAmount] = useState(PRESETS[1]!);
  const [customAmount, setCustomAmount] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  const [creating, setCreating] = useState(false);

  // LLMR-116: which leg startTopUp runs — Omise.createSource + {source} vs
  // Omise.createToken + {token}. Card details below are only ever read when
  // this is 'card', and never sent anywhere but Omise.
  const [method, setMethod] = useState<'promptpay' | 'card'>('promptpay');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  // MM/YY as two small fields rather than one free-text one — fewer ways to
  // type something Omise's expiration_month/year won't parse.
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear, setCardExpYear] = useState('');
  const [cardCVC, setCardCVC] = useState('');
  // Loose, client-side only: catches an obviously-incomplete form before a
  // round-trip to Omise. The real validation is Omise's — a card that passes
  // this and still fails tokenizes into an OmiseSourceError below.
  const cardDigits = cardNumber.replace(/\s+/g, '');
  const cardValid =
    method !== 'card' ||
    (cardName.trim() !== '' &&
      /^\d{12,19}$/.test(cardDigits) &&
      /^(0?[1-9]|1[0-2])$/.test(cardExpMonth) &&
      /^\d{2}$/.test(cardExpYear) &&
      /^\d{3,4}$/.test(cardCVC));
  // Dropdowns instead of free-text MM/YY — nothing to mistype, and the
  // options are naturally short lists. 15 years is comfortably past any
  // card's real max validity (issuers rarely go past 5-7).
  const cardExpMonthOptions = [
    { value: '', label: 'MM' },
    ...Array.from({ length: 12 }, (_, i) => {
      const mm = String(i + 1).padStart(2, '0');
      return { value: mm, label: mm };
    }),
  ];
  const cardExpYearOptions = [
    { value: '', label: 'YY' },
    ...Array.from({ length: 15 }, (_, i) => {
      const yy = String(new Date().getFullYear() + i).slice(-2);
      return { value: yy, label: yy };
    }),
  ];

  // Distinct from `creating`: the customer has committed and the payment
  // exists, we are waiting on Omise and then on our own charge call. Worth its
  // own message because it is the slowest step and the screen is otherwise
  // blank between "Continue to pay" and the QR appearing.
  const [tokenizing, setTokenizing] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [savingQr, setSavingQr] = useState(false);
  const [saveQrError, setSaveQrError] = useState<string | null>(null);

  // LLMR-122: company_qr_payload is raw EMVCo text, not an image URL like
  // payment_qr — it has to be drawn client-side, same as EditProfile.tsx
  // already does for the TOTP enrollment QR.
  const [companyQrImage, setCompanyQrImage] = useState<string | null>(null);

  // LLMR-121: which top-up (if any) the slip-upload modal is open for. A
  // Set rather than a single flag for "already submitted" — the customer
  // can recover more than one stuck top-up from their history in one visit.
  const [slipUploadTopUpId, setSlipUploadTopUpId] = useState<string | null>(null);
  const [slipSubmittedFor, setSlipSubmittedFor] = useState<Set<string>>(new Set());

  // LLMR-235: b2b withholding-tax claim-back. eligibleWHT holds the current
  // checkbox picker's data (GET /topups/wht-eligible) — a top-up disappears
  // from it the moment a claim is created against it, so this is reloaded
  // after every successful submission rather than derived locally. selected
  // is which rows are checked right now; whtClaimTopUpIds (when non-null)
  // opens the modal preselecting exactly that set — a lone row's own
  // "Claim" button passes just itself, the bulk button passes `selected`.
  const [eligibleWHT, setEligibleWHT] = useState<components['schemas']['WHTEligibleTopUp'][]>([]);
  const [selectedWHT, setSelectedWHT] = useState<Set<string>>(new Set());
  const [whtClaimTopUpIds, setWhtClaimTopUpIds] = useState<Set<string> | null>(null);
  const [whtClaims, setWhtClaims] = useState<components['schemas']['WHTClaimSummary'][] | null>(null);
  const [whtClaimsError, setWhtClaimsError] = useState<string | null>(null);
  const [whtEligibleError, setWhtEligibleError] = useState<string | null>(null);
  const whtSelectAllRef = useRef<HTMLInputElement>(null);

  // LLMR-90: e-Tax invoice mock — collected here, before the payment leg,
  // per the requirement's "collect at top-up request time" flow. buyerName/
  // buyerEmail are always required; the rest only when `taxInvoiceFull` is
  // ticked. This is a DRAFT document, not a real tax invoice — see
  // taxInvoiceDraftNote in the form below.
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  // Prefills name/email from the signed-in account, for a customer who
  // would rather not type them — most top-ups are the account holder buying
  // their own credit anyway. Does NOT lock the fields: the prefilled value
  // is a starting point, not a constraint — a customer may still want to
  // edit or add to it (e.g. append a company suffix to their account name).
  // Unchecking clears both fields back to empty.
  const [useAccountInfo, setUseAccountInfo] = useState(false);
  const [taxInvoiceFull, setTaxInvoiceFull] = useState(false);
  // Drives which fields the full-invoice section asks for: an individual
  // has no branch at all, and a juristic buyer's tax ID is a business
  // registration number rather than a national ID card number.
  const [buyerType, setBuyerType] = useState<'individual' | 'juristic'>('individual');
  const [taxId, setTaxId] = useState('');
  // Defaults to head office — the common case, and itself a valid 5-digit
  // code, so a juristic customer who never touches this field still submits
  // cleanly. Not shown at all for an individual — see branchCodeEffective.
  const [branchCode, setBranchCode] = useState('00000');
  // Only asked (and only sent) for a juristic buyer whose branch isn't head
  // office — head office is identified by its code alone, and an individual
  // has no branch to name.
  const [branchName, setBranchName] = useState('');
  const [addressHouseNo, setAddressHouseNo] = useState('');
  // Cascading province -> district -> subdistrict picker. Each level's
  // options come from llmr-shared's vendored geography data (member-api's
  // /geo/* — see pkg/geo); only the CODE is kept as state, and the Thai
  // name/postal code sent to the server is looked up from the fetched list
  // below, same shape the free-text fields used to send.
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [subdistricts, setSubdistricts] = useState<Subdistrict[]>([]);
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<number | null>(null);
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<number | null>(null);
  const [selectedSubdistrictCode, setSelectedSubdistrictCode] = useState<number | null>(null);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [subdistrictsLoading, setSubdistrictsLoading] = useState(false);
  // Guards the one-shot province fetch below — a ref (not state) so a failed
  // fetch can be retried by unsetting it, without re-render churn on success.
  const provincesFetchedRef = useRef(false);
  // The org's latest full-invoice profile, if any (null until fetched, and
  // stays null forever for an org that has never submitted one — the
  // checkbox below only renders when this is non-null). Mutually exclusive
  // with useAccountInfo: both are competing sources for buyerName/buyerEmail.
  const [taxProfile, setTaxProfile] = useState<TaxInvoiceProfile | null>(null);
  const [useTaxProfile, setUseTaxProfile] = useState(false);
  const taxProfileFetchedRef = useRef(false);

  const selectedSubdistrict = subdistricts.find((s) => s.code === selectedSubdistrictCode) ?? null;
  // Dropdown labels follow the UI locale; the payload still sends Thai names
  // (name_th) because tax invoices are issued in Thai. Codes travel alongside
  // so the server can re-resolve if needed.
  const geoLabel = (item: { name_en: string; name_th: string }) =>
    locale === 'th' ? item.name_th : item.name_en;
  const addressProvince = provinces.find((p) => p.code === selectedProvinceCode)?.name_th ?? '';
  const addressDistrict = districts.find((d) => d.code === selectedDistrictCode)?.name_th ?? '';
  const addressSubdistrict = selectedSubdistrict?.name_th ?? '';
  const addressPostalCode = selectedSubdistrict ? String(selectedSubdistrict.postal_code) : '';
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  const buyerNameTrimmed = buyerName.trim();
  const buyerEmailTrimmed = buyerEmail.trim();
  const taxIdTrimmed = normalizeTaxId(taxId.trim());
  const branchCodeTrimmed = branchCode.trim();
  const branchNameTrimmed = branchName.trim();
  // An individual has no branch — the code sent/validated is always head
  // office regardless of what (if anything) is in the now-hidden field.
  const branchCodeEffective = buyerType === 'individual' ? '00000' : branchCodeTrimmed;
  // Only a juristic buyer's non-head-office branch gets a name — head
  // office is identified by its code alone, and an individual has no
  // branch at all.
  const branchNameRequired = buyerType === 'juristic' && branchCodeEffective !== '00000';

  const buyerBasicsValid = buyerNameTrimmed !== '' && buyerEmailTrimmed !== '' && buyerEmailTrimmed.includes('@');
  // Only checked (and only shown) once there is something to check against —
  // an empty tax-ID field is "not filled in yet", not "invalid".
  const taxIdShowError = taxInvoiceFull && taxIdTrimmed !== '' && !isValidTaxId(taxIdTrimmed);
  // Same "don't scold an empty field" rule as taxIdShowError — but branchCode
  // defaults to a valid "00000", so in practice this only fires once the
  // customer has actually typed something invalid. Only shown for juristic —
  // an individual never sees this field at all.
  const branchCodeShowError =
    taxInvoiceFull && buyerType === 'juristic' && branchCodeTrimmed !== '' && !BRANCH_CODE_RE.test(branchCodeTrimmed);
  const taxInvoiceFullValid =
    !taxInvoiceFull ||
    (isValidTaxId(taxIdTrimmed) &&
      BRANCH_CODE_RE.test(branchCodeEffective) &&
      (!branchNameRequired || branchNameTrimmed !== '') &&
      addressHouseNo.trim() !== '' &&
      addressSubdistrict.trim() !== '' &&
      addressDistrict.trim() !== '' &&
      addressProvince.trim() !== '' &&
      addressPostalCode.trim() !== '');
  const taxInvoiceValid = buyerBasicsValid && taxInvoiceFullValid;

  const [active, setActive] = useState<TopUpResource | null>(null);
  const qrCountdown = useQrCountdown(active?.status === 'pending' ? active.expires_at : null);
  // Client clock hit zero before the next poll flips status to expired — hide
  // the QR immediately so nobody keeps scanning a dead code.
  const qrLocallyExpired = active?.status === 'pending' && qrCountdown.expired;
  // History rows keep the create-time snapshot until a poll/refresh lands.
  // Mirror the active card's local expiry so the matching row flips to
  // "หมดอายุ" as soon as the countdown hits zero — the server reconciler
  // can lag ~30s behind expires_at, and a refresh alone would still show
  // pending until then.
  function historyStatus(t: TopUpResource): TopUpResource['status'] {
    if (t.topup_id === active?.topup_id && qrLocallyExpired && t.status === 'pending') {
      return 'expired';
    }
    return t.status;
  }

  // LLMR-122: draws the company QR once per distinct payload, not on every
  // poll tick — polling re-fetches the same top-up (and so the same
  // payload string) every few seconds while it stays pending, and toDataURL
  // is real, if cheap, work worth skipping when nothing changed.
  const companyQrPayload = active?.status === 'pending' ? (active.company_qr_payload ?? null) : null;
  useEffect(() => {
    if (!companyQrPayload) {
      setCompanyQrImage(null);
      return;
    }
    let cancelled = false;
    void toDataURL(companyQrPayload, { margin: 1, width: 260 }).then((url) => {
      if (!cancelled) setCompanyQrImage(url);
    });
    return () => {
      cancelled = true;
    };
  }, [companyQrPayload]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks the topup_id the two refs below apply to, so switching to a
  // different active top-up (startAnother) resets the wait rather than
  // inheriting a stale count from the previous one.
  const paidWaitTopUpIdRef = useRef<string | null>(null);
  const paidPollCountRef = useRef(0);

  // Payment history now comes from the server (GET /topups, keyset-paged).
  // It used to be React state holding only the top-ups started in this tab,
  // so a customer who refreshed while a PromptPay payment was pending lost
  // sight of it entirely.
  const [history, setHistory] = useState<TopUpResource[]>([]);
  const [historyPage, setHistoryPage] = useState<PageInfo | null>(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoadedOnce, setHistoryLoadedOnce] = useState(false);
  const [historyFrom, setHistoryFrom] = useState(defaultHistoryFrom);
  const [historyTo, setHistoryTo] = useState(() => todayIsoDay());
  const [historyPageIndex, setHistoryPageIndex] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(DEFAULT_HISTORY_PAGE_SIZE);
  const historyCursors = useRef<(string | undefined)[]>([undefined]);
  const historyFilteredAll = useRef<TopUpResource[]>([]);
  const historyServerPaging = useRef(true);
  const historyLoadGen = useRef(0);
  const historyPageIndexRef = useRef(historyPageIndex);
  historyPageIndexRef.current = historyPageIndex;

  // LLMR-61: whether to show FirstTopUpPrompt once this session's top-up
  // reaches "paid". Captured from a one-row unfiltered probe — an empty
  // result means no top-up has ever existed on this account.
  const firstLoadDone = useRef(false);
  const [neverToppedUp, setNeverToppedUp] = useState<boolean | null>(null);
  const shownFirstPromptRef = useRef(false);
  const [showFirstPrompt, setShowFirstPrompt] = useState(false);

  const loadHistoryPage = useCallback(
    async (targetPage: number, size = historyPageSize, refetchAll = false) => {
      const gen = ++historyLoadGen.current;
      setHistoryBusy(true);
      setHistoryError(null);
      try {
        if (historyServerPaging.current) {
          const res = await api.topUps({
            limit: size,
            cursor: historyCursors.current[targetPage - 1],
            from: rangeStart(historyFrom),
            to: rangeEnd(historyTo),
          });
          if (gen !== historyLoadGen.current) return;

          if (res.page.total != null) {
            setHistory(res.data);
            setHistoryPage(res.page);
            setHistoryPageIndex(targetPage);
            historyCursors.current[targetPage] = res.page.next_cursor ?? undefined;
            setHistoryLoadedOnce(true);
            return;
          }

          // Older member-api ignores from/to and omits page.total — filter here.
          historyServerPaging.current = false;
          historyCursors.current = [undefined];
        }

        if (refetchAll || targetPage === 1 || historyFilteredAll.current.length === 0) {
          const all = await fetchAllTopUps();
          if (gen !== historyLoadGen.current) return;
          historyFilteredAll.current = filterTopUpsByRange(all, historyFrom, historyTo);
        }

        const { slice, page } = clientHistoryPage(historyFilteredAll.current, targetPage, size);
        if (gen !== historyLoadGen.current) return;
        setHistory(slice);
        setHistoryPage(page);
        setHistoryPageIndex(targetPage);
        setHistoryLoadedOnce(true);
      } catch (e) {
        if (gen !== historyLoadGen.current) return;
        setHistoryError(
          e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.topUp.historyLoadError,
        );
      } finally {
        if (gen === historyLoadGen.current) setHistoryBusy(false);
      }
    },
    [historyFrom, historyTo, historyPageSize, dict.topUp.historyLoadError],
  );

  const resetAndLoadHistory = useCallback(() => {
    historyCursors.current = [undefined];
    historyFilteredAll.current = [];
    historyServerPaging.current = true;
    void loadHistoryPage(1, historyPageSize, true);
  }, [loadHistoryPage, historyPageSize]);

  const refreshHistory = useCallback(() => {
    void loadHistoryPage(historyPageIndexRef.current, historyPageSize, true);
  }, [loadHistoryPage, historyPageSize]);

  const refreshWHTEligible = useCallback(() => {
    setWhtEligibleError(null);
    void api
      .whtEligibleTopUps()
      .then((res) => setEligibleWHT(res.data))
      .catch((e) => {
        setWhtEligibleError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.whtClaim.eligibleLoadError);
      });
  }, [dict.whtClaim.eligibleLoadError]);

  const refreshWHTClaims = useCallback(() => {
    setWhtClaimsError(null);
    void api
      .whtClaims()
      .then((res) => setWhtClaims(res.data))
      .catch((e) => {
        setWhtClaimsError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.whtClaim.historyLoadError);
        setWhtClaims([]);
      });
  }, [dict.whtClaim.historyLoadError]);

  // B2B orgs always issue invoices in a company name; the individual button
  // is hidden for them, so ensure buyerType is 'juristic' whenever the org
  // is confirmed B2B (the session loads async, so this may fire after mount).
  useEffect(() => {
    if (isB2BOrg) setBuyerType('juristic');
  }, [isB2BOrg]);

  // b2b orgs only, same gate as the endpoints themselves — a plain
  // individual-type org never sees a claim button or claim history at all.
  useEffect(() => {
    if (!ready || !hasOrg || !canManageBilling || !isB2BOrg) return;
    refreshWHTEligible();
    refreshWHTClaims();
  }, [ready, hasOrg, canManageBilling, isB2BOrg, refreshWHTEligible, refreshWHTClaims]);

  // Fires from either path a top-up can reach "paid" through — the poll
  // effect below, or a synchronous charge response that is already paid by
  // the time chargeTopUp returns — without duplicating the check in both.
  useEffect(() => {
    if (active?.status === 'paid' && neverToppedUp === true && !shownFirstPromptRef.current) {
      shownFirstPromptRef.current = true;
      setShowFirstPrompt(true);
    }
  }, [active?.status, neverToppedUp]);

  useEffect(() => {
    if (!ready) return;
    if (!hasOrg) {
      setOnboarding(true);
      return;
    }
    // LLMR-140: GET /topups is admin/owner only (LLMR-55, same gate as GET
    // /balance) — a plain member 403s here, same as the panels below that
    // already check canManageBilling. Clearing history rather than just
    // skipping the fetch is what actually fixes the reported bug: without
    // this, switching from Owner to a plain member on an already-open
    // /topup tab left the previously-fetched rows sitting in state (and so
    // still on screen, historyLoadedOnce having already flipped true)
    // since nothing here used to depend on role at all.
    if (!canManageBilling) {
      setHistory([]);
      setHistoryLoadedOnce(false);
      return;
    }
    if (!firstLoadDone.current) {
      firstLoadDone.current = true;
      void api
        .topUps({ limit: 1 })
        .then((res) => setNeverToppedUp(res.data.length === 0))
        .catch(() => {});
    }
    historyCursors.current = [undefined];
    historyFilteredAll.current = [];
    historyServerPaging.current = true;
    void loadHistoryPage(1, historyPageSize, true);
  }, [ready, hasOrg, canManageBilling, historyFrom, historyTo, loadHistoryPage, historyPageSize]);

  // LLMR-116: resumes a card top-up after a 3-D Secure redirect. This tab's
  // own state is gone — the customer's browser was on the bank's page — so
  // `return_uri` (`/topup?topup_return=<id>`) is the only thing that
  // survives the round trip. Re-fetching by id and handing the result to
  // `active` re-enters the exact same "pending, poll until it settles"
  // path a same-tab charge already goes through; nothing here assumes the
  // redirect itself means success.
  useEffect(() => {
    if (!ready || !hasOrg) return;
    const params = new URLSearchParams(window.location.search);
    const topUpID = params.get('topup_return');
    if (!topUpID) return;
    // Strip the param immediately so a manual refresh re-polls GET
    // /topups/{id} instead of re-triggering this effect from a stale URL.
    window.history.replaceState(null, '', window.location.pathname);
    void api
      .topUp(topUpID)
      .then(setActive)
      .catch(() => {
        // The top-up may have expired or the id may be stale — the customer
        // still has the full form and their history table to fall back on.
      });
  }, [ready, hasOrg]);

  // Loads the province list the first time the full-invoice fields become
  // visible — not on every TopUp page visit, since most customers never tick
  // "full tax invoice". provincesFetchedRef (not provinces.length) guards
  // this so a failed fetch can retry on the next toggle without depending on
  // `provinces` itself and refetching on every render once it's populated.
  useEffect(() => {
    if (!taxInvoiceFull || provincesFetchedRef.current) return;
    provincesFetchedRef.current = true;
    void api.geo
      .provinces()
      .then((res) => setProvinces(res.data))
      .catch(() => {
        provincesFetchedRef.current = false;
      });
  }, [taxInvoiceFull]);

  // Loads the org's latest full-invoice profile the first time the
  // full-invoice fields become visible — same one-shot pattern as the
  // province list above. Auto-applies it (and switches off useAccountInfo;
  // the two checkboxes are mutually exclusive prefill sources) per product
  // decision: re-typing an unchanged profile every top-up is exactly what
  // this checkbox exists to avoid. Leaves `taxProfile` null (so the
  // checkbox never renders) for an org that has never submitted one.
  useEffect(() => {
    if (!taxInvoiceFull || taxProfileFetchedRef.current) return;
    taxProfileFetchedRef.current = true;
    void api
      .taxInvoiceProfile()
      .then((res) => {
        if (!res.exists) return;
        setTaxProfile(res);
        setUseTaxProfile(true);
        setUseAccountInfo(false);
        void applyTaxProfile(res);
      })
      .catch(() => {
        taxProfileFetchedRef.current = false;
      });
    // applyTaxProfile is a plain function, recreated every render, but this
    // effect is guarded by taxProfileFetchedRef to run at most once per
    // taxInvoiceFull transition — listing it as a dependency would only add
    // re-run churn, never change what the effect actually does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxInvoiceFull]);

  async function handleProvinceChange(code: number) {
    setSelectedProvinceCode(code);
    setSelectedDistrictCode(null);
    setSelectedSubdistrictCode(null);
    setDistricts([]);
    setSubdistricts([]);
    setDistrictsLoading(true);
    try {
      const res = await api.geo.districts(code);
      setDistricts(res.data);
    } finally {
      setDistrictsLoading(false);
    }
  }

  async function handleDistrictChange(code: number) {
    setSelectedDistrictCode(code);
    setSelectedSubdistrictCode(null);
    setSubdistricts([]);
    setSubdistrictsLoading(true);
    try {
      const res = await api.geo.subdistricts(code);
      setSubdistricts(res.data);
    } finally {
      setSubdistrictsLoading(false);
    }
  }

  // Restores a saved profile into every full-invoice field, reusing the
  // exact same cascade the manual dropdown flow uses (handleProvinceChange
  // then handleDistrictChange) rather than duplicating the fetch logic —
  // the profile carries pkg/geo codes precisely so this can call them
  // programmatically instead of re-deriving codes from saved names.
  async function applyTaxProfile(profile: TaxInvoiceProfile) {
    if (profile.buyer_type) setBuyerType(profile.buyer_type);
    setBuyerName(profile.buyer_name ?? '');
    setBuyerEmail(profile.buyer_email ?? '');
    setTaxId(profile.tax_id ?? '');
    setBranchCode(profile.branch_code ?? '00000');
    setBranchName(profile.branch_name ?? '');
    setAddressHouseNo(profile.address?.house_no ?? '');
    const { province_code: provinceCode, district_code: districtCode, subdistrict_code: subdistrictCode } =
      profile.address ?? {};
    if (provinceCode == null || districtCode == null || subdistrictCode == null) return;
    await handleProvinceChange(provinceCode);
    await handleDistrictChange(districtCode);
    setSelectedSubdistrictCode(subdistrictCode);
  }

  const effectiveAmount = usingCustom ? customAmount : amount;
  let amountError: string | null = null;
  let belowMinimum = false;
  const trimmed = effectiveAmount.trim();
  if (trimmed === '') {
    amountError = dict.topUp.enterAmount;
  } else {
    try {
      const micro = toMicro(trimmed);
      if (micro <= 0n) amountError = dict.topUp.amountMustBePositive;
      else if (micro < toMicro(MIN_TOP_UP)) {
        belowMinimum = true;
        amountError = dict.topUp.belowMinimum(MIN_TOP_UP_THB);
      }
    } catch (e) {
      amountError = e instanceof MoneyParseError ? dict.topUp.notValidTHB : dict.topUp.notValidAmount;
    }
  }
  // Live validation copy only for Custom amounts below the minimum — presets
  // are always valid, and an empty Custom field just disables the button.
  const showBelowMinimum = usingCustom && belowMinimum;

  // VAT-exclusive top-up pricing: `trimmed` is the ex-VAT amount the
  // customer entered/selected (and what lands in the wallet); the actual
  // charge is 7% more. Computed client-side only for this preview —
  // member-api computes the authoritative figure server-side and this must
  // always agree with it, which is why grossUpVAT7 mirrors the Go
  // implementation's banker's rounding exactly rather than estimating.
  let vatInclusiveAmount: string | null = null;
  if (!amountError) {
    try {
      vatInclusiveAmount = grossUpVAT7(trimmed);
    } catch {
      vatInclusiveAmount = null;
    }
  }

  // Poll the active top-up until it leaves "pending", THEN — separately —
  // for a bounded while longer if it landed on "paid" with no invoice_id
  // yet. Credit is added only by the signature-verified Omise webhook,
  // never inferred here; this poll exists purely to refresh what the
  // SERVER says, and to pull a fresh balance once it says "paid".
  //
  // The second half exists because of LLMR-90: the draft invoice is
  // generated by a background goroutine AFTER the webhook already answered
  // "credited", so `status` can read 'paid' before `invoice_id` is set.
  // Without this, the customer only sees the download button after
  // manually reloading the page.
  useEffect(() => {
    if (active?.topup_id !== paidWaitTopUpIdRef.current) {
      paidWaitTopUpIdRef.current = active?.topup_id ?? null;
      paidPollCountRef.current = 0;
    }

    const waitingForInvoice =
      active?.status === 'paid' && !active.invoice_id && paidPollCountRef.current < MAX_PAID_POLLS_FOR_INVOICE;

    if (!active || (active.status !== 'pending' && !waitingForInvoice)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const wasPending = active.status === 'pending';
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const updated = await api.topUp(active.topup_id);
          setActive(updated);
          if (updated.status === 'paid') {
            if (wasPending) {
              void refreshBalance();
              showToast(dict.topUp.topUpSuccessToast);
              // This top-up just became "paid" — it may now be WHT-eligible
              // (>=1,000 THB ex-VAT, b2b org). eligibleWHT was last fetched
              // on page load / the last claim submission, so without this it
              // would only pick up the new row after a manual reload.
              if (isB2BOrg) void refreshWHTEligible();
            }
            if (updated.invoice_id) {
              // Either the pending->paid transition (first paid sighting)
              // or the invoice just appeared — either way the history row
              // changed under us. Re-read it rather than patching the
              // local copy.
              void refreshHistory();
            } else {
              paidPollCountRef.current += 1;
              if (wasPending) void refreshHistory(); // still show the "paid" status even if the invoice isn't ready yet
              if (paidPollCountRef.current >= MAX_PAID_POLLS_FOR_INVOICE) void refreshHistory(); // last attempt, give up waiting
            }
          } else if (wasPending && updated.status !== 'pending') {
            // expired / failed — the history table still holds the pending
            // snapshot from create-time unless we re-fetch it here.
            void refreshHistory();
          }
        } catch {
          // A transient poll failure is not worth surfacing — the next tick
          // tries again, and the customer can always refresh the balance
          // manually once payment has actually gone through.
        }
      })();
    }, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [active, refreshBalance, refreshHistory, showToast, dict.topUp.topUpSuccessToast, isB2BOrg, refreshWHTEligible]);

  // Countdown hit zero — don't wait up to POLL_MS for the next tick; ask the
  // server once now so active + history can converge on expired sooner.
  // Display already flips via historyStatus / qrLocallyExpired either way.
  useEffect(() => {
    if (!qrLocallyExpired || !active) return;
    const topupId = active.topup_id;
    let cancelled = false;
    void (async () => {
      try {
        const updated = await api.topUp(topupId);
        if (cancelled) return;
        setActive(updated);
        if (updated.status !== 'pending') void refreshHistory();
      } catch {
        // Next poll tick retries; local expiry display already covers the gap.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qrLocallyExpired, active?.topup_id, refreshHistory]);

  async function startTopUp(e: React.FormEvent) {
    e.preventDefault();
    if (amountError || !taxInvoiceValid) return;
    setCreating(true);
    setCreateError(null);
    try {
      const taxInvoice: TaxInvoiceInput = taxInvoiceFull
        ? {
            full: true,
            buyer_name: buyerNameTrimmed,
            buyer_email: buyerEmailTrimmed,
            buyer_type: buyerType,
            tax_id: taxIdTrimmed,
            branch_code: branchCodeEffective,
            branch_name: branchNameRequired ? branchNameTrimmed : '',
            address: {
              house_no: addressHouseNo.trim(),
              subdistrict: addressSubdistrict.trim(),
              subdistrict_code: selectedSubdistrictCode ?? undefined,
              district: addressDistrict.trim(),
              district_code: selectedDistrictCode ?? undefined,
              province: addressProvince.trim(),
              province_code: selectedProvinceCode ?? undefined,
              postal_code: addressPostalCode.trim(),
            },
          }
        : { full: false, buyer_name: buyerNameTrimmed, buyer_email: buyerEmailTrimmed };

      // Leg one: register the payment. No QR yet, and no money has moved.
      const created = await api.createTopUp(trimmed, idempotencyKey, taxInvoice, method);
      setActive(created);
      setIdempotencyKey(newIdempotencyKey());
      // A new pending row now exists server-side; reload from page 1 rather
      // than prepending, so the list matches what the server would return.
      void resetAndLoadHistory();

      // Legs two and three. `public_key` is absent when the server had nothing
      // left to do — an idempotency replay of an already-charged top-up, which
      // arrives with its payment_qr already set.
      if (created.public_key && created.amount_satang) {
        setTokenizing(true);
        // Card details never reach our servers — Omise.createToken() sends
        // them straight to Omise from here, and only the resulting token id
        // goes to chargeTopUp. PromptPay carries no card data at all, but it
        // is the same two-step shape either way.
        const charged =
          method === 'card'
            ? await (async () => {
                const token = await createCardToken(created.public_key!, {
                  name: cardName.trim(),
                  number: cardDigits,
                  expirationMonth: Number.parseInt(cardExpMonth, 10),
                  expirationYear: 2000 + Number.parseInt(cardExpYear, 10),
                  securityCode: cardCVC.trim(),
                });
                return api.chargeTopUp(created.topup_id, { token });
              })()
            : await (async () => {
                const source = await createPromptPaySource(created.public_key!, created.amount_satang!);
                return api.chargeTopUp(created.topup_id, { source });
              })();
        setActive(charged);
        // A card charge that needs 3-D Secure hands back a redirect instead
        // of settling here — nothing left for this tab to do but send the
        // customer to their bank. It sends them back to `return_uri`
        // (`?topup_return=`, handled in the resume effect below), which
        // picks this same top-up back up by id.
        if (charged.authorize_uri) {
          window.location.href = charged.authorize_uri;
          return;
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setOnboarding(true);
      else if (e instanceof OmiseUnavailableError) setCreateError(dict.topUp.paymentUnavailable);
      else if (e instanceof OmiseSourceError) setCreateError(dict.topUp.sourceError);
      else if (e instanceof ApiError && e.status === 409) setCreateError(dict.topUp.paymentExpired);
      else if (e instanceof ApiError && e.status === 503) setCreateError(dict.topUp.paymentsNotConfigured);
      else setCreateError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.topUp.createError);
      // The pending row is left alone deliberately: it expires on its own, and
      // the customer's next attempt is a fresh top-up rather than a resumed
      // one. Nothing here has taken a payment.
    } finally {
      setCreating(false);
      setTokenizing(false);
    }
  }

  function startAnother() {
    setActive(null);
    setCreateError(null);
    setSaveQrError(null);
    // Card details are never sent anywhere but Omise, and there is no reason
    // to leave a typed card number sitting in memory once its top-up is
    // done — clear it the same way a checkout page would.
    setCardName('');
    setCardNumber('');
    setCardExpMonth('');
    setCardExpYear('');
    setCardCVC('');
    // The finished top-up is already in the server's history; no need to keep
    // a second copy of it in component state.
    void resetAndLoadHistory();
  }

  // LLMR-90: mints a presigned URL for a DRAFT invoice PDF and downloads it.
  // The URL carries response-content-disposition=attachment (objstore.go),
  // so it never renders as a page — the browser saves it and the current tab
  // simply stays put. That means no new tab is needed (and so no popup
  // blocker to work around, and no leftover blank tab once the save starts).
  async function downloadInvoice(invoiceId: string) {
    if (downloadingInvoiceId) return;
    setDownloadingInvoiceId(invoiceId);
    setInvoiceError(null);
    try {
      const { download_url } = await reportApi.downloadInvoice(invoiceId);
      window.location.href = download_url;
    } catch (e) {
      setInvoiceError(
        e instanceof ApiError && e.status === 409 ? dict.topUp.invoiceComingSoon : dict.topUp.invoiceDownloadError,
      );
    } finally {
      setDownloadingInvoiceId(null);
    }
  }

  // Shared by both QR sources: the Omise-issued payment_qr (a URL) and the
  // LLMR-122 company QR (a data URL already drawn client-side into
  // companyQrImage) — downloadQrImage itself already handles either kind of
  // src transparently, so only the filename prefix needs to differ.
  async function saveQrCode(src: string, fileNamePrefix: string, framed: boolean) {
    if (savingQr) return;
    setSavingQr(true);
    setSaveQrError(null);
    try {
      const amountLabel = formatTHB(active!.amount_charged.amount);
      await downloadQrImage(src, {
        framed,
        title: framed ? dict.topUp.saveQrImageTitle : '',
        amountLine: amountLabel,
        payeeName: framed ? (active!.company_qr_payee_name ?? null) : null,
        expiresLine: active!.expires_at ? dict.topUp.expires(formatDateTime(active!.expires_at)) : null,
        // Timestamped rather than amount-based: two top-ups for the same
        // amount used to silently overwrite each other's saved file
        // (browsers add a "(1)" suffix at best, or just overwrite depending
        // on OS/download settings) — the moment saved is always unique.
        fileName: `${fileNamePrefix}-${fileTimestamp(new Date())}.png`,
      });
    } catch {
      setSaveQrError(dict.topUp.saveQrError);
    } finally {
      setSavingQr(false);
    }
  }

  // "Select all" for the WHT checkbox column — scoped to the current page's
  // eligible rows only (history is one page at a time), not every eligible
  // top-up across the whole account. Lives as a standalone toolbar control
  // above the table rather than inside <thead>, deliberately: this table's
  // <thead> is visually clipped to 1x1px on narrow viewports (every
  // .table-scroll in the app collapses to a card layout below 860px, see
  // styles.css) — a checkbox placed there would be reachable by a screen
  // reader but literally unclickable by a mouse/touch user on the layout
  // this app is mobile-first for.
  const whtEligibleIdsOnPage = history
    .filter((t) => eligibleWHT.some((e) => e.topup_id === t.topup_id))
    .map((t) => t.topup_id);
  const whtAllSelected =
    whtEligibleIdsOnPage.length > 0 && whtEligibleIdsOnPage.every((id) => selectedWHT.has(id));
  const whtSomeSelected = whtEligibleIdsOnPage.some((id) => selectedWHT.has(id));
  useEffect(() => {
    if (whtSelectAllRef.current) {
      whtSelectAllRef.current.indeterminate = whtSomeSelected && !whtAllSelected;
    }
  }, [whtSomeSelected, whtAllSelected]);

  return (
    <div className="page billing-page">
      <PageHeading subtitle={dict.topUp.subtitle}>{dict.common.topUp}</PageHeading>

      {onboarding ? (
        <OnboardingNotice area={dict.onboarding.topupArea} />
      ) : (
        <>
          <motion.section
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="panel panel--hero billing-panel billing-panel--hero"
          >
            <p className="billing-hero-kicker">{dict.topUp.currentBalance}</p>
            {canManageBilling ? (
              <>
                <div className="balance">
                  {balance === null ? <PageLoader compact /> : formatTHB(balance)}
                </div>
                {balanceUpdatedAt && (
                  <p className="note">{dict.topUp.asOf(formatDateTime(balanceUpdatedAt))}</p>
                )}
              </>
            ) : (
              // LLMR-55: same restriction as Dashboard's hero panel — without
              // this the loader above spins forever, since GET /balance
              // 403s for a plain member and `balance` never leaves null.
              <p className="note">{dict.topUp.balanceRestricted(isB2BOrg)}</p>
            )}
          </motion.section>

          <AutoFallbackToggle />

          {canManageBilling && <PromoCodeRedeem />}

          <AnimatePresence mode="wait">
            {active ? (
              <motion.section
                key="active"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0 }}
                className="panel billing-panel billing-panel--active"
              >
                <div className="panel-heading-row">
                  <h2>{dict.topUp.inProgress}</h2>
                  {active.status === 'pending' && (
                    <button
                      type="button"
                      className="ghost btn-icon panel-back-btn"
                      onClick={startAnother}
                      aria-label={dict.topUp.backToForm}
                      title={dict.topUp.backToForm}
                    >
                      <ArrowLeftOutlined aria-hidden="true" />
                    </button>
                  )}
                </div>
                <div className="topup-status">
                  <span className={`badge badge--${qrLocallyExpired ? 'expired' : active.status}`}>
                    {dict.topUp.status[qrLocallyExpired ? 'expired' : active.status]}
                  </span>
                  <span className="balance-inline">฿{formatBalance(active.amount_charged.amount)}</span>
                </div>

                {active.status === 'pending' &&
                  !qrLocallyExpired &&
                  (active.payment_qr || companyQrPayload) && (
                  <p className="qr-wait-status" role="status">
                    <LoadingOutlined spin aria-hidden="true" />
                    <span>{dict.topUp.waitingForPayment}</span>
                  </p>
                )}

                {active.status === 'pending' &&
                  qrCountdown.label !== null &&
                  !qrLocallyExpired &&
                  (active.payment_qr || companyQrPayload) && (
                  <p className="qr-countdown" aria-live="polite">
                    {dict.topUp.timeRemaining(qrCountdown.label)}
                  </p>
                )}

                {active.status === 'pending' && !active.payment_qr && tokenizing && (
                  <p className="sub" role="status">
                    {active.method === 'card' ? dict.topUp.preparingCardPayment : dict.topUp.preparingPayment}
                  </p>
                )}

                {active.status === 'pending' && !active.payment_qr && !tokenizing && active.method === 'card' && (
                  <p className="sub" role="status">
                    {dict.topUp.awaitingCardConfirmation}
                  </p>
                )}

                {active.status === 'pending' && !qrLocallyExpired && active.payment_qr && (
                  <div className="qr-panel">
                    <div className="qr-pay-layout">
                      <div className="qr-pay-how">
                        <h3 className="qr-pay-how-title">{dict.topUp.payHowTitle}</h3>
                        <ol className="qr-pay-how-steps">
                          {dict.topUp.payHowSteps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                        <p className="note qr-pay-how-success">{dict.topUp.payHowSuccessNote}</p>
                      </div>
                      <div className="qr-pay-visual">
                        <QRCard
                          framed={false}
                          qrSrc={active.payment_qr}
                          qrAlt={dict.topUp.qrAlt}
                          amountLabel={formatTHB(active.amount_charged.amount)}
                        />
                      </div>
                    </div>
                    <div className="qr-pay-actions">
                      <button
                        type="button"
                        className="ghost btn-icon qr-save-btn"
                        onClick={() => void saveQrCode(api.topUpQrImageUrl(active.topup_id), 'llmr-promptpay', false)}
                        disabled={savingQr}
                      >
                        <DownloadOutlined aria-hidden="true" />
                        {savingQr ? dict.topUp.saveQrSaving : dict.topUp.saveQrCode}
                      </button>
                      {slipSubmittedFor.has(active.topup_id) ? (
                        <p className="note">{dict.topUp.uploadSlipSuccess}</p>
                      ) : (
                        <button type="button" className="ghost" onClick={() => setSlipUploadTopUpId(active.topup_id)}>
                          {dict.topUp.uploadSlipButton}
                        </button>
                      )}
                    </div>
                    {saveQrError && (
                      <p className="note warn-text" role="alert">
                        {saveQrError}
                      </p>
                    )}
                  </div>
                )}

                {active.status === 'pending' && !qrLocallyExpired && companyQrPayload && (
                  <div className="qr-panel">
                    {companyQrImage && (
                      <QRCard
                        qrSrc={companyQrImage}
                        qrAlt={dict.topUp.companyQrAlt}
                        amountLabel={formatTHB(active.amount_charged.amount)}
                        payeeName={active.company_qr_payee_name}
                      />
                    )}
                    <p className="note">{dict.topUp.companyQrNote}</p>
                    {companyQrImage && (
                      <button
                        type="button"
                        className="ghost btn-icon qr-save-btn"
                        onClick={() => void saveQrCode(companyQrImage, 'llmr-company-promptpay', true)}
                        disabled={savingQr}
                      >
                        <DownloadOutlined aria-hidden="true" />
                        {savingQr ? dict.topUp.saveQrSaving : dict.topUp.saveQrCode}
                      </button>
                    )}
                    {saveQrError && (
                      <p className="note warn-text" role="alert">
                        {saveQrError}
                      </p>
                    )}
                  </div>
                )}

                {active.status === 'paid' && <p className="sub">{dict.topUp.creditAdded}</p>}
                {(active.status === 'expired' || qrLocallyExpired) && (
                  <p className="sub warn-text">{dict.topUp.expiredGuidance}</p>
                )}
                {active.status === 'failed' && <p className="sub warn-text">{dict.topUp.failed}</p>}

                {(active.status !== 'pending' ||
                  qrLocallyExpired ||
                  (companyQrPayload && !active.payment_qr)) && (
                  <div className="topup-result-actions">
                    {(active.status !== 'pending' || qrLocallyExpired) && (
                      <button type="button" onClick={startAnother}>
                        {dict.topUp.startAnother}
                      </button>
                    )}
                    {(active.status === 'expired' ||
                      qrLocallyExpired ||
                      active.status === 'failed' ||
                      (active.status === 'pending' && companyQrPayload && !active.payment_qr)) &&
                      (slipSubmittedFor.has(active.topup_id) ? (
                        <p className="note">{dict.topUp.uploadSlipSuccess}</p>
                      ) : (
                        <button type="button" className="ghost" onClick={() => setSlipUploadTopUpId(active.topup_id)}>
                          {companyQrPayload ? dict.topUp.companyQrUploadPrompt : dict.topUp.uploadSlipButton}
                        </button>
                      ))}
                  </div>
                )}
              </motion.section>
            ) : !canManageBilling ? (
              // LLMR-55: a plain member can never submit this form — the
              // backend rejects POST /topups with 403 — so it is not shown
              // rather than shown-and-rejected on click.
              <motion.section
                key="restricted"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0 }}
                className="panel billing-panel billing-panel--form"
              >
                <h2>{dict.topUp.topUpRestrictedTitle}</h2>
                <p className="note">{dict.topUp.topUpRestrictedBody(isB2BOrg)}</p>
              </motion.section>
            ) : (
              <motion.section
                key="form"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0 }}
                className="panel billing-panel billing-panel--form"
              >
                <h2>{dict.topUp.addCredit}</h2>
                <form onSubmit={startTopUp} className="billing-form">
                  <div className="form-step">
                  <div className="form-step-header">
                    <span className="form-step-badge" aria-hidden="true">1</span>
                    <span className="form-step-label">{dict.topUp.stepAmountLabel}</span>
                  </div>
                  <div className="amount-presets">
                    {PRESETS.map((p) => (
                      <button
                        type="button"
                        key={p}
                        className={`chip${!usingCustom && amount === p ? ' chip--active' : ''}`}
                        onClick={() => {
                          setAmount(p);
                          setUsingCustom(false);
                          setCreateError(null);
                          setIdempotencyKey(newIdempotencyKey());
                        }}
                      >
                        ฿{p.replace('.00', '')}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`chip${usingCustom ? ' chip--active' : ''}`}
                      onClick={() => {
                        setUsingCustom(true);
                        setCreateError(null);
                        setIdempotencyKey(newIdempotencyKey());
                      }}
                    >
                      {dict.topUp.custom}
                    </button>
                  </div>

                  {usingCustom && (
                    <div className="inline-form-field">
                      <label htmlFor="custom-amount">{dict.topUp.amountLabel}<Req /></label>
                      <input
                        id="custom-amount"
                        inputMode="decimal"
                        pattern="[0-9]*[.]?[0-9]*"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(sanitizeAmountInput(e.target.value));
                          setCreateError(null);
                          setIdempotencyKey(newIdempotencyKey());
                        }}
                        placeholder={dict.topUp.amountPlaceholder}
                        autoFocus
                        required
                      />
                      {showBelowMinimum && (
                        <div className="field-error">{amountError}</div>
                      )}
                    </div>
                  )}

                  {vatInclusiveAmount && (
                    <p className="note topup-vat-note">
                      {dict.topUp.vatInclusiveNote(formatBalance(vatInclusiveAmount))}
                    </p>
                  )}
                  </div>

                  <div className="form-step">
                  <div className="form-step-header">
                    <span className="form-step-badge" aria-hidden="true">2</span>
                    <span className="form-step-label">{dict.topUp.paymentMethodLabel}</span>
                  </div>
                  <div className="payment-method-grid" role="radiogroup" aria-label={dict.topUp.paymentMethodLabel}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === 'promptpay'}
                      className={`payment-method-card${method === 'promptpay' ? ' payment-method-card--active' : ''}`}
                      onClick={() => {
                        setMethod('promptpay');
                        setCreateError(null);
                        setIdempotencyKey(newIdempotencyKey());
                      }}
                    >
                      <span className="payment-method-check" aria-hidden="true">
                        {method === 'promptpay' && <CheckOutlined />}
                      </span>
                      <QrcodeOutlined className="payment-method-icon" aria-hidden="true" />
                      <span className="payment-method-title">{dict.topUp.methodPromptPay}</span>
                      {locale !== 'en' && <span className="payment-method-subtitle">{dict.topUp.methodPromptPaySubtitle}</span>}
                      <span className="pay-marks" aria-hidden="true">
                        <PromptPayMark />
                      </span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={method === 'card'}
                      className={`payment-method-card${method === 'card' ? ' payment-method-card--active' : ''}`}
                      onClick={() => {
                        setMethod('card');
                        setCreateError(null);
                        setIdempotencyKey(newIdempotencyKey());
                      }}
                    >
                      <span className="payment-method-check" aria-hidden="true">
                        {method === 'card' && <CheckOutlined />}
                      </span>
                      <CreditCardOutlined className="payment-method-icon" aria-hidden="true" />
                      <span className="payment-method-title">{dict.topUp.methodCard}</span>
                      {locale !== 'en' && <span className="payment-method-subtitle">{dict.topUp.methodCardSubtitle}</span>}
                      <span className="pay-marks" aria-hidden="true">
                        <VisaMark />
                        <MastercardMark />
                        <JcbMark />
                      </span>
                    </button>
                  </div>

                  {method === 'card' ? (
                    <div className="card-details-fields">
                      <div className="tax-invoice-full-fields">
                        <div className="inline-form-field">
                          <label htmlFor="card-name">{dict.topUp.cardNameLabel}<Req /></label>
                          <input
                            id="card-name"
                            value={cardName}
                            onChange={(e) => setCardName(e.target.value)}
                            placeholder={dict.topUp.cardNamePlaceholder}
                            autoComplete="cc-name"
                            required
                          />
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="card-number">{dict.topUp.cardNumberLabel}<Req /></label>
                          <input
                            id="card-number"
                            inputMode="numeric"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value.replace(/[^\d ]/g, ''))}
                            placeholder={dict.topUp.cardNumberPlaceholder}
                            autoComplete="cc-number"
                            required
                          />
                        </div>
                      </div>
                      <div className="tax-invoice-full-fields">
                        <div className="inline-form-field">
                          <label htmlFor="card-exp-month">{dict.topUp.cardExpiryLabel}<Req /></label>
                          <div className="card-expiry-row">
                            <SelectMenu
                              id="card-exp-month"
                              aria-label={dict.topUp.cardExpiryLabel}
                              value={cardExpMonth}
                              onChange={setCardExpMonth}
                              options={cardExpMonthOptions}
                            />
                            <span aria-hidden="true">/</span>
                            <SelectMenu
                              id="card-exp-year"
                              aria-label={dict.topUp.cardExpiryLabel}
                              value={cardExpYear}
                              onChange={setCardExpYear}
                              options={cardExpYearOptions}
                            />
                          </div>
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="card-cvc">{dict.topUp.cardCVCLabel}<Req /></label>
                          <input
                            id="card-cvc"
                            inputMode="numeric"
                            value={cardCVC}
                            onChange={(e) => setCardCVC(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder={dict.topUp.cardCVCPlaceholder}
                            autoComplete="cc-csc"
                            maxLength={4}
                            required
                          />
                        </div>
                      </div>
                      <p className="note">{dict.topUp.paidByCard}</p>
                    </div>
                  ) : (
                    <div className="payment-method-hint">
                      <p className="note">{dict.topUp.paidByPromptPay}</p>
                      <p className="note">{dict.topUp.paidByPromptPayExpiry(QR_TTL_MINUTES)}</p>
                    </div>
                  )}
                  </div>

                  <div className="form-step">
                  <div className="form-step-header">
                    <span className="form-step-badge" aria-hidden="true">3</span>
                    <span className="form-step-label">{dict.topUp.taxInvoiceHeading}</span>
                  </div>
                    <p className="note">{dict.topUp.taxInvoiceDraftNote}</p>

                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={useAccountInfo}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseAccountInfo(checked);
                          // Mutually exclusive with useTaxProfile below —
                          // both are competing prefill sources for these
                          // same two fields.
                          if (checked) setUseTaxProfile(false);
                          // Unchecking means "let me type something else" —
                          // clear rather than leave the account's values
                          // sitting there looking hand-typed.
                          setBuyerName(checked ? me?.display_name || me?.email || '' : '');
                          setBuyerEmail(checked ? me?.email || '' : '');
                        }}
                      />
                      {dict.topUp.useAccountInfoLabel}
                    </label>

                    <div className="inline-form-field">
                      <label htmlFor="buyer-name">
                        {taxInvoiceFull && buyerType === 'juristic' ? dict.topUp.buyerCompanyNameLabel : dict.topUp.buyerNameLabel}
                        <Req />
                      </label>
                      <input
                        id="buyer-name"
                        value={buyerName}
                        onChange={(e) => setBuyerName(e.target.value)}
                        placeholder={
                          taxInvoiceFull && buyerType === 'juristic'
                            ? dict.topUp.buyerCompanyNamePlaceholder
                            : dict.topUp.buyerNamePlaceholder
                        }
                        required
                      />
                    </div>
                    <div className="inline-form-field">
                      <label htmlFor="buyer-email">{dict.topUp.buyerEmailLabel}<Req /></label>
                      <input
                        id="buyer-email"
                        type="email"
                        value={buyerEmail}
                        onChange={(e) => setBuyerEmail(e.target.value)}
                        placeholder={dict.topUp.buyerEmailPlaceholder}
                        required
                      />
                    </div>

                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={taxInvoiceFull}
                        onChange={(e) => setTaxInvoiceFull(e.target.checked)}
                      />
                      {dict.topUp.taxInvoiceFullLabel}
                    </label>

                    {taxInvoiceFull && taxProfile && (
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={useTaxProfile}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setUseTaxProfile(checked);
                            if (checked) {
                              // Mutually exclusive with useAccountInfo —
                              // both are competing prefill sources for
                              // buyer name/email.
                              setUseAccountInfo(false);
                              void applyTaxProfile(taxProfile);
                              return;
                            }
                            // Unchecking means "let me type something
                            // else" — clear every field this populated,
                            // same "don't leave prefilled values sitting
                            // there looking hand-typed" rule useAccountInfo
                            // follows.
                            setBuyerType('individual');
                            setBuyerName('');
                            setBuyerEmail('');
                            setTaxId('');
                            setBranchCode('00000');
                            setBranchName('');
                            setAddressHouseNo('');
                            setSelectedProvinceCode(null);
                            setSelectedDistrictCode(null);
                            setSelectedSubdistrictCode(null);
                            setDistricts([]);
                            setSubdistricts([]);
                          }}
                        />
                        {dict.topUp.useTaxProfileLabel}
                      </label>
                    )}

                    {taxInvoiceFull && (
                      <div className="tax-invoice-full-fields">
                        <div className="inline-form-field">
                          <label>{dict.topUp.buyerTypeLabel}</label>
                          <div className="buyer-type-chips" role="radiogroup" aria-label={dict.topUp.buyerTypeLabel}>
                            {!isB2BOrg && (
                              <button
                                type="button"
                                role="radio"
                                aria-checked={buyerType === 'individual'}
                                className={`chip${buyerType === 'individual' ? ' chip--active' : ''}`}
                                onClick={() => setBuyerType('individual')}
                              >
                                {dict.topUp.buyerTypeIndividual}
                              </button>
                            )}
                            <button
                              type="button"
                              role="radio"
                              aria-checked={buyerType === 'juristic'}
                              className={`chip${buyerType === 'juristic' ? ' chip--active' : ''}`}
                              onClick={() => setBuyerType('juristic')}
                            >
                              {dict.topUp.buyerTypeJuristic}
                            </button>
                          </div>
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="tax-id">
                            {buyerType === 'juristic' ? dict.topUp.taxIdLabelJuristic : dict.topUp.taxIdLabelIndividual}
                            <Req />
                          </label>
                          <input
                            id="tax-id"
                            inputMode="numeric"
                            maxLength={13}
                            value={taxId}
                            // Digits only, capped at 13 — same rule as branch code below.
                            onChange={(e) => setTaxId(e.target.value.replace(/\D/g, '').slice(0, 13))}
                            placeholder={
                              buyerType === 'juristic' ? dict.topUp.taxIdPlaceholderJuristic : dict.topUp.taxIdPlaceholderIndividual
                            }
                            required
                          />
                          {taxIdShowError && <div className="field-error">{dict.topUp.taxIdInvalid}</div>}
                        </div>
                        {buyerType === 'juristic' && (
                          <div className="inline-form-field">
                            <label htmlFor="branch-code">{dict.topUp.branchCodeLabel}<Req /></label>
                            <input
                              id="branch-code"
                              inputMode="numeric"
                              value={branchCode}
                              // Digits only, capped at 5 — the field itself
                              // demonstrates the rule rather than making the
                              // customer guess it from an error message.
                              onChange={(e) => setBranchCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                              placeholder={dict.topUp.branchCodePlaceholder}
                              required
                            />
                            {branchCodeShowError && <div className="field-error">{dict.topUp.branchCodeInvalid}</div>}
                          </div>
                        )}
                        {branchNameRequired && (
                          <div className="inline-form-field">
                            <label htmlFor="branch-name">{dict.topUp.branchNameLabel}<Req /></label>
                            <input
                              id="branch-name"
                              value={branchName}
                              onChange={(e) => setBranchName(e.target.value)}
                              placeholder={dict.topUp.branchNamePlaceholder}
                              required
                            />
                          </div>
                        )}
                        <div className="inline-form-field">
                          <label htmlFor="address-house-no">{dict.topUp.addressHouseNoLabel}<Req /></label>
                          <input
                            id="address-house-no"
                            value={addressHouseNo}
                            onChange={(e) => setAddressHouseNo(e.target.value)}
                            placeholder={dict.topUp.addressHouseNoPlaceholder}
                            required
                          />
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="address-province">{dict.topUp.addressProvinceLabel}<Req /></label>
                          <SelectMenu
                            id="address-province"
                            aria-label={dict.topUp.addressProvinceLabel}
                            searchable
                            searchHint={dict.topUp.addressSelectTypeHint}
                            noResultsLabel={dict.topUp.addressSelectNoResults}
                            clearLabel={dict.common.clear}
                            value={selectedProvinceCode !== null ? String(selectedProvinceCode) : ''}
                            onChange={(value) => {
                              if (value === '') {
                                setSelectedProvinceCode(null);
                                setSelectedDistrictCode(null);
                                setSelectedSubdistrictCode(null);
                                setDistricts([]);
                                setSubdistricts([]);
                                return;
                              }
                              void handleProvinceChange(Number(value));
                            }}
                            options={[
                              { value: '', label: dict.topUp.addressProvincePlaceholder },
                              ...provinces.map((p) => ({ value: String(p.code), label: geoLabel(p) })),
                            ]}
                          />
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="address-district">{dict.topUp.addressDistrictLabel}<Req /></label>
                          <SelectMenu
                            id="address-district"
                            aria-label={dict.topUp.addressDistrictLabel}
                            searchable
                            searchHint={dict.topUp.addressSelectTypeHint}
                            noResultsLabel={dict.topUp.addressSelectNoResults}
                            clearLabel={dict.common.clear}
                            value={selectedDistrictCode !== null ? String(selectedDistrictCode) : ''}
                            disabled={selectedProvinceCode === null || districtsLoading}
                            onChange={(value) => {
                              if (value === '') {
                                setSelectedDistrictCode(null);
                                setSelectedSubdistrictCode(null);
                                setSubdistricts([]);
                                return;
                              }
                              void handleDistrictChange(Number(value));
                            }}
                            options={
                              selectedProvinceCode === null
                                ? [{ value: '', label: dict.topUp.addressDistrictSelectProvinceFirst }]
                                : [
                                    { value: '', label: dict.topUp.addressDistrictPlaceholder },
                                    ...districts.map((d) => ({ value: String(d.code), label: geoLabel(d) })),
                                  ]
                            }
                          />
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="address-subdistrict">{dict.topUp.addressSubdistrictLabel}<Req /></label>
                          <SelectMenu
                            id="address-subdistrict"
                            aria-label={dict.topUp.addressSubdistrictLabel}
                            searchable
                            searchHint={dict.topUp.addressSelectTypeHint}
                            noResultsLabel={dict.topUp.addressSelectNoResults}
                            clearLabel={dict.common.clear}
                            value={selectedSubdistrictCode !== null ? String(selectedSubdistrictCode) : ''}
                            disabled={selectedDistrictCode === null || subdistrictsLoading}
                            onChange={(value) => setSelectedSubdistrictCode(value === '' ? null : Number(value))}
                            options={
                              selectedDistrictCode === null
                                ? [{ value: '', label: dict.topUp.addressSubdistrictSelectDistrictFirst }]
                                : [
                                    { value: '', label: dict.topUp.addressSubdistrictPlaceholder },
                                    ...subdistricts.map((sd) => ({
                                      value: String(sd.code),
                                      label: geoLabel(sd),
                                    })),
                                  ]
                            }
                          />
                        </div>
                        <div className="inline-form-field">
                          <label htmlFor="address-postal-code">{dict.topUp.addressPostalCodeLabel}<Req /></label>
                          <input
                            id="address-postal-code"
                            inputMode="numeric"
                            value={addressPostalCode}
                            placeholder={dict.topUp.addressPostalCodePlaceholder}
                            disabled
                          />
                        </div>
                      </div>
                    )}
                    {taxInvoiceFull && !isB2BOrg && buyerType === 'juristic' && (
                      <p className="note" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.4em', color: 'var(--warn)' }}>
                        <InfoCircleFilled aria-hidden="true" style={{ flexShrink: 0, marginTop: '0.15em' }} />
                        <span>{dict.topUp.juristicWhtNote}</span>
                      </p>
                    )}
                  </div>

                  {createError && (
                    <div className="err">{createError}</div>
                  )}

                  {method === 'promptpay' && (
                    <div className="topup-next-callout">
                      <div className="topup-next-callout-header">
                        <span className="topup-next-badge">{dict.topUp.afterContinueBadge}</span>
                        <span className="topup-next-title">{dict.topUp.afterContinueTitle}</span>
                      </div>
                      <ol className="topup-next-steps">
                        {dict.topUp.afterContinueSteps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {vatInclusiveAmount && (
                    <p className="note topup-vat-summary">
                      {dict.topUp.vatSummaryBeforeConfirm(formatBalance(vatInclusiveAmount))}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={creating || !!amountError || !taxInvoiceValid || !cardValid}
                    className="btn-block"
                  >
                    {creating
                      ? dict.topUp.starting
                      : dict.topUp.continueToPay(vatInclusiveAmount ? formatBalance(vatInclusiveAmount) : trimmed || '0.00')}
                  </button>
                </form>
              </motion.section>
            )}
          </AnimatePresence>

          {canManageBilling && historyLoadedOnce && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel billing-panel billing-panel--history">
              <h2>{dict.topUp.historyHeading}</h2>
              <p className="note">{dict.topUp.historyNote}</p>
              {invoiceError && (
                <p className="note warn-text" role="alert">
                  {invoiceError}
                </p>
              )}

              <div className="usage-filters billing-history-filters">
                <div className="inline-form-field">
                  <label htmlFor="topup-history-from">{dict.reports.fromLabel}</label>
                  <DateField
                    id="topup-history-from"
                    value={historyFrom}
                    max={historyTo}
                    rangeEnd={historyTo}
                    onChange={setHistoryFrom}
                    aria-label={dict.reports.fromLabel}
                  />
                </div>
                <div className="inline-form-field">
                  <label htmlFor="topup-history-to">{dict.reports.toLabel}</label>
                  <DateField
                    id="topup-history-to"
                    value={historyTo}
                    min={historyFrom}
                    max={todayIsoDay()}
                    rangeStart={historyFrom}
                    onChange={setHistoryTo}
                    aria-label={dict.reports.toLabel}
                  />
                </div>
              </div>

              {historyError && <div className="err" role="alert">{historyError}</div>}

              {historyBusy && history.length === 0 && <PageLoader compact />}

              {!historyBusy && history.length === 0 && (
                <EmptyState title={dict.topUp.historyEmptyTitle}>{dict.topUp.historyEmptyBody}</EmptyState>
              )}

              {isB2BOrg && whtEligibleIdsOnPage.length > 0 && (
                <div
                  className="toolbar"
                  style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}
                >
                  <label className="checkbox-label">
                    <input
                      ref={whtSelectAllRef}
                      type="checkbox"
                      checked={whtAllSelected}
                      onChange={() =>
                        setSelectedWHT((prev) => {
                          const next = new Set(prev);
                          if (whtAllSelected) {
                            whtEligibleIdsOnPage.forEach((id) => next.delete(id));
                          } else {
                            whtEligibleIdsOnPage.forEach((id) => next.add(id));
                          }
                          return next;
                        })
                      }
                    />
                    {dict.whtClaim.selectAllLabel}
                  </label>
                  {selectedWHT.size > 0 && (
                    <button type="button" className="ghost" onClick={() => setWhtClaimTopUpIds(new Set(selectedWHT))}>
                      {dict.whtClaim.claimSelectedButton(selectedWHT.size)}
                    </button>
                  )}
                </div>
              )}
              {whtEligibleError && <div className="err" role="alert">{whtEligibleError}</div>}

              {history.length > 0 && (
              <div className="table-scroll">
                <table role="table" className="billing-table">
                  <thead role="rowgroup">
                    <tr role="row">
                      {isB2BOrg && <th role="columnheader">{dict.whtClaim.colSelect}</th>}
                      {isB2BOrg && <th role="columnheader">{dict.whtClaim.colSubmitDoc}</th>}
                      <th role="columnheader">{dict.topUp.colStarted}</th>
                      <th role="columnheader" className="num billing-col-amount">
                        {dict.topUp.colAmount}
                      </th>
                      <th role="columnheader" className="billing-col-status">
                        {dict.topUp.colStatus}
                      </th>
                      <th role="columnheader">{dict.topUp.colInvoice}</th>
                      <th role="columnheader" className="billing-col-action">
                        {dict.topUp.colAction}
                      </th>
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {history.map((t) => {
                      const status = historyStatus(t);
                      const whtEligible = eligibleWHT.find((e) => e.topup_id === t.topup_id) ?? null;
                      return (
                        <tr role="row" key={t.topup_id}>
                          {isB2BOrg && (
                            <td role="cell" className="billing-col-action" data-label={dict.whtClaim.colSelect}>
                              {whtEligible && (
                                <input
                                  type="checkbox"
                                  aria-label={dict.whtClaim.claimButton}
                                  checked={selectedWHT.has(t.topup_id)}
                                  onChange={() =>
                                    setSelectedWHT((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(t.topup_id)) next.delete(t.topup_id);
                                      else next.add(t.topup_id);
                                      return next;
                                    })
                                  }
                                />
                              )}
                            </td>
                          )}
                          {isB2BOrg && (
                            <td role="cell" className="billing-col-action" data-label={dict.whtClaim.colSubmitDoc}>
                              {whtEligible && (
                                <button
                                  type="button"
                                  className="ghost btn-icon-only"
                                  onClick={() => setWhtClaimTopUpIds(new Set([t.topup_id]))}
                                  aria-label={dict.whtClaim.claimButton}
                                  data-tooltip={dict.whtClaim.claimButton}
                                >
                                  <UploadOutlined aria-hidden="true" />
                                </button>
                              )}
                            </td>
                          )}
                          <td role="cell" data-label={dict.topUp.colStarted}>
                            {formatDateTime(t.created_at)}
                          </td>
                          <td role="cell" className="num billing-col-amount" data-label={dict.topUp.colAmount}>
                            <span className="billing-amount">฿{formatBalance(t.amount.amount)}</span>
                            {(() => {
                              // VAT-exclusive top-up pricing: amount is the ex-VAT
                              // credit, amount_charged the real total paid — the
                              // difference is what VAT actually came to. Hidden
                              // (not shown as ฿0.00) for a row from before this
                              // migration's backfill, where amount_charged always
                              // equals amount and there truly was no VAT charged.
                              const vatMicro = toMicro(t.amount_charged.amount) - toMicro(t.amount.amount);
                              if (vatMicro <= 0n) return null;
                              return (
                                <span className="billing-vat-note">
                                  {dict.topUp.colAmountVatNote(formatBalance(toDecimalString(vatMicro)))}
                                </span>
                              );
                            })()}
                          </td>
                          <td role="cell" className="billing-col-status" data-label={dict.topUp.colStatus}>
                            <span className={`badge badge--${status}`}>{dict.topUp.status[status]}</span>
                          </td>
                          <td role="cell" data-label={dict.topUp.colInvoice}>
                            {/* invoice_id is populated once LLMR-90's draft mock is
                                generated — see TopUp.invoice_id's contract note.
                                Not a real tax document: automated e-Tax issuance is
                                still gated on the tax advisor confirming blueprint B9
                                in writing.

                                paid-but-no-invoice_id is genuinely ambiguous from a
                                history row alone: it means "still generating" for a
                                few seconds on a top-up that just went through in
                                THIS session, but it also means "predates the
                                feature, will never have one" for anything older —
                                the API has no timestamp that tells them apart (no
                                paid_at in the contract). Only show the "generating"
                                placeholder for the row this session is actively
                                polling (t.topup_id === active?.topup_id); every
                                other paid-but-invoice-less row renders nothing,
                                same as before this feature existed, rather than a
                                placeholder that would sit there forever and lie
                                about still being in progress. */}
                            {t.status === 'paid' &&
                              (t.invoice_id ? (
                                <button
                                  type="button"
                                  className="ghost btn-icon-only"
                                  disabled={downloadingInvoiceId === t.invoice_id}
                                  onClick={() => void downloadInvoice(t.invoice_id!)}
                                  aria-label={downloadingInvoiceId === t.invoice_id ? dict.topUp.starting : dict.topUp.invoiceButton}
                                  data-tooltip={downloadingInvoiceId === t.invoice_id ? dict.topUp.starting : dict.topUp.invoiceButton}
                                >
                                  {downloadingInvoiceId === t.invoice_id ? (
                                    <LoadingOutlined spin aria-hidden="true" />
                                  ) : (
                                    <DownloadOutlined aria-hidden="true" />
                                  )}
                                </button>
                              ) : t.topup_id === active?.topup_id ? (
                                <button
                                  type="button"
                                  className="ghost btn-icon-only"
                                  disabled
                                  aria-label={dict.topUp.invoiceGenerating}
                                  data-tooltip={dict.topUp.invoiceComingSoon}
                                  aria-disabled="true"
                                >
                                  <LoadingOutlined spin aria-hidden="true" />
                                </button>
                              ) : null)}
                          </td>
                          <td role="cell" className="billing-col-action" data-label={dict.topUp.colAction}>
                            {(status === 'expired' || status === 'failed') &&
                              (slipSubmittedFor.has(t.topup_id) ? (
                                <span className="note">{dict.topUp.uploadSlipSuccess}</span>
                              ) : (
                                <button
                                  type="button"
                                  className="ghost btn-icon-only"
                                  onClick={() => setSlipUploadTopUpId(t.topup_id)}
                                  aria-label={dict.topUp.uploadSlipButton}
                                  data-tooltip={dict.topUp.uploadSlipButton}
                                >
                                  <UploadOutlined aria-hidden="true" />
                                </button>
                              ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}

              {history.length > 0 && (
              <CursorPagePager
                page={historyPage}
                loaded={history.length}
                pageIndex={historyPageIndex}
                busy={historyBusy}
                onPrev={() => void loadHistoryPage(historyPageIndex - 1)}
                onNext={() => void loadHistoryPage(historyPageIndex + 1)}
                pageSize={historyPageSize}
                onPageSizeChange={(size) => {
                  setHistoryPageSize(size);
                  historyCursors.current = [undefined];
                  void loadHistoryPage(1, size, !historyServerPaging.current);
                }}
              />
              )}
            </motion.section>
          )}

          {isB2BOrg && canManageBilling && (
            <motion.section
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="panel billing-panel billing-panel--history"
            >
              <h2>{dict.whtClaim.historyHeading}</h2>
              <p className="note">{dict.whtClaim.historyLede}</p>

              {whtClaimsError && <div className="err" role="alert">{whtClaimsError}</div>}
              {whtClaims === null && <PageLoader compact />}
              {whtClaims?.length === 0 && <EmptyState title={dict.whtClaim.historyEmpty} />}

              {whtClaims && whtClaims.length > 0 && (
                <div className="table-scroll">
                  <table role="table" className="billing-table">
                    <thead role="rowgroup">
                      <tr role="row">
                        <th role="columnheader">{dict.whtClaim.colDocNumber}</th>
                        <th role="columnheader">{dict.whtClaim.colDocDate}</th>
                        <th role="columnheader" className="num">{dict.whtClaim.colDeclaredAmount}</th>
                        <th role="columnheader" className="num">{dict.whtClaim.colComputedAmount}</th>
                        <th role="columnheader">{dict.whtClaim.colStatus}</th>
                        <th role="columnheader">{dict.whtClaim.colReason}</th>
                      </tr>
                    </thead>
                    <tbody role="rowgroup">
                      {whtClaims.map((c) => (
                        <tr role="row" key={c.claim_id}>
                          <td role="cell" className="wrap" data-label={dict.whtClaim.colDocNumber}>
                            {c.doc_number}
                          </td>
                          <td role="cell" data-label={dict.whtClaim.colDocDate}>
                            {formatIsoDate(c.doc_date)}
                          </td>
                          <td role="cell" className="num" data-label={dict.whtClaim.colDeclaredAmount}>
                            ฿{formatBalance(c.declared_amount.amount)}
                          </td>
                          <td role="cell" className="num" data-label={dict.whtClaim.colComputedAmount}>
                            ฿{formatBalance(c.total_expected_wht.amount)}
                          </td>
                          <td role="cell" data-label={dict.whtClaim.colStatus}>
                            <span
                              className={`badge badge--${
                                c.status === 'pending_review' ? 'pending' : c.status === 'approved' ? 'paid' : 'failed'
                              }`}
                            >
                              {dict.whtClaim.statusLabels[c.status]}
                            </span>
                          </td>
                          <td role="cell" className="wrap" data-label={dict.whtClaim.colReason}>
                            {c.rejection_reason ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.section>
          )}
        </>
      )}

      {showFirstPrompt && <FirstTopUpPrompt onDone={() => setShowFirstPrompt(false)} />}

      <AnimatePresence>
        {slipUploadTopUpId && (
          <SlipUploadModal
            topUpId={slipUploadTopUpId}
            onClose={() => setSlipUploadTopUpId(null)}
            onDone={(result) => {
              if (result === 'submitted') {
                setSlipSubmittedFor((prev) => new Set(prev).add(slipUploadTopUpId));
              }
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {whtClaimTopUpIds && (
          <WHTClaimModal
            eligible={eligibleWHT}
            initialSelectedIds={whtClaimTopUpIds}
            onClose={() => setWhtClaimTopUpIds(null)}
            onDone={(claimedTopUpIds) => {
              setWhtClaimTopUpIds(null);
              setSelectedWHT((prev) => {
                const next = new Set(prev);
                claimedTopUpIds.forEach((id) => next.delete(id));
                return next;
              });
              refreshWHTEligible();
              refreshWHTClaims();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
